import React, { useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import { i18nLangToThemeLocale } from '../../../assets/utils/comm/languageConstants';

/**
 * Footer 하단(Copyright) 영역 기본 데이터 (JSON 형식, 추후 DB 연동 시 동일 구조로 로드)
 */
const DEFAULT_FOOTER_BOTTOM_JSON = {
  copyright: {
    kor: 'Copyright @ RISE Center, Hankuk University of Foreign Studies. All Rights Reserved.',
    eng: 'Copyright @ RISE Center, Hankuk University of Foreign Studies. All Rights Reserved.'
  },
  links: [
    { kor: '개인정보처리방침', eng: 'Privacy Policy', link: '/privacy' },
    { kor: '이용약관', eng: 'Terms of Use', link: '/terms' },
    { kor: '오픈소스 사용정보', eng: 'Open Source Notice', link: '/open-source-notice' }
  ]
};

/**
 * FooterBottom 컴포넌트
 * Footer의 Copyright 영역을 담당하는 컴포넌트
 *
 * @param {Object} props
 * @param {boolean} props.isGlowTheme - GLOW 테마 여부
 * @param {Object} props.copyright - Copyright 데이터 (기존 테마용, DEFAULT_FOOTER_BOTTOM_JSON.copyright 형식)
 * @param {Object} props.commonData - 공통 데이터 (GLOW 테마용, commonData.copyright 형식)
 */
const FooterBottom = memo(({ isGlowTheme, copyright, commonData }) => {
  const themeContext = useTheme();
  const { i18n } = useTranslation();
  // window.__themeConfig를 우선 확인 (DB에서 로드한 값)
  const dbConfig = typeof window !== 'undefined' && window.__themeConfig ? window.__themeConfig : {};
  const themeConfig = themeContext?.themeConfig || dbConfig || {};

  // 영역별 컨텐츠: commonData.copyright 또는 copyright (구조: { copyright: { kor, eng }, links: [...] })
  const defaultData = DEFAULT_FOOTER_BOTTOM_JSON;
  const resolved = isGlowTheme
    ? { copyright: { ...defaultData.copyright, ...commonData?.copyright?.copyright }, links: commonData?.copyright?.links ?? defaultData.links }
    : { copyright: { ...defaultData.copyright, ...copyright?.copyright }, links: copyright?.links ?? defaultData.links };
  const locale = i18nLangToThemeLocale(i18n.language);
  const copyrightText = (resolved.copyright && (resolved.copyright[locale] ?? resolved.copyright.kor)) || '';
  const footerLinks = Array.isArray(resolved.links) ? resolved.links : defaultData.links;

  // Copyright 정렬 적용 (원본 footer.applyCopyrightAlignment과 동일)
  useEffect(() => {
    const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
    const copyrightP = copyrightEl ? copyrightEl.querySelector('p') : null;

    if (copyrightEl && copyrightP) {
      const alignment = themeConfig.copyrightAlignment || 'center';

      // flex 레이아웃이므로 justify-content 사용
      if (alignment === 'left') {
        copyrightP.style.justifyContent = 'flex-start';
        copyrightP.style.textAlign = 'left';
      } else if (alignment === 'center') {
        copyrightP.style.justifyContent = 'center';
        copyrightP.style.textAlign = 'center';
      } else if (alignment === 'right') {
        copyrightP.style.justifyContent = 'flex-end';
        copyrightP.style.textAlign = 'right';
      }
    }
  }, [themeConfig.copyrightAlignment]);

  const linkLabel = (item) => item[locale] ?? item.kor ?? item.text ?? '';

  if (isGlowTheme) {
    // GLOW 테마 Copyright 영역: 저작권 좌측, 하단 링크 우측
    return (
      <div className="footer-copyright-area">
        <div className="container">
          <div className="footer-copyright-content">
            <div className="footer-copyright-left">
              {copyrightText && (
                <span className="footer-copyright-text">{copyrightText}</span>
              )}
            </div>
            <nav className="footer-copyright-links" aria-label="Footer legal links">
              {footerLinks.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.link || '#'}
                  className="footer-copyright-link"
                >
                  {linkLabel(item)}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>
    );
  }

  // 기존 테마 Copyright 영역 (simple, modern, vibrant)
  return (
    <div
      className="bg-gray-3"
      style={{
        backgroundColor: themeConfig.copyrightBackgroundColor,
        color: themeConfig.copyrightTextColor,
        borderTop: '1px solid #e0e0e0'
      }}
    >
      <div className="container">
        <div
          className="footer-bottom copyright"
          style={{
            paddingTop: `${themeConfig.copyrightHeight || 10}px`,
            paddingBottom: `${themeConfig.copyrightHeight || 10}px`,
            color: themeConfig.copyrightTextColor,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            width: '100%'
          }}
        >
          <div className="footer-copyright-left" style={{ textAlign: 'left' }}>
            {copyrightText && (
              <span style={{ color: themeConfig.copyrightTextColor || '#999' }}>{copyrightText}</span>
            )}
          </div>
          <nav
            className="footer-copyright-links"
            aria-label="Footer legal links"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px',
              color: themeConfig.copyrightTextColor || '#999'
            }}
          >
            {footerLinks.map((item, idx) => (
              <Link
                key={idx}
                to={item.link || '#'}
                style={{
                  color: 'inherit',
                  textDecoration: 'none'
                }}
              >
                {linkLabel(item)}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
});

FooterBottom.displayName = 'FooterBottom';

export { DEFAULT_FOOTER_BOTTOM_JSON };
export default FooterBottom;

